import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { GridCellProps } from "@progress/kendo-react-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import allocationApi from "../../api/allocation";
import { LoadingIndicator } from "../LoadingIndicator";

interface AllocationReassignColumnProps extends GridCellProps {
  dataItem: any;
  companies: any[];
  onReassigned?: () => void;
}

export const AllocationReassignColumn = (props: AllocationReassignColumnProps) => {
  const { dataItem, companies, onReassigned } = props;
  const [showDialog, setShowDialog] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);

  const queryClient = useQueryClient();

  // Use TanStack React Query mutation for better error handling and cache management
  const updateCompanyMutation = useMutation({
    mutationFn: ({ allocationId, newCompanyId }: { allocationId: number; newCompanyId: number }) =>
      allocationApi.updateAllocationCompany(allocationId, newCompanyId),
    onSuccess: (data) => {
      setShowDialog(false);
      setShowWarning(false);

      // Invalidate and refetch allocations to ensure UI is up to date
      queryClient.invalidateQueries({ queryKey: ["allocations"] });

      onReassigned?.();
    },
    onError: (error) => {
      // Error handling is handled by the UI state
    },
  });

  const handleProceedWithReassignment = async () => {
    if (!selectedCompanyId) return;

    updateCompanyMutation.mutate({
      allocationId: dataItem.allocationId,
      newCompanyId: selectedCompanyId,
    });
  };

  const handleSendClick = () => {
    setShowDialog(true);
    setSelectedCompanyId(null);
  };

  const handleConfirm = async () => {
    if (!selectedCompanyId) return;

    // Show warning instead of proceeding directly
    setShowWarning(true);
  };

  const handleCancel = () => {
    setShowDialog(false);
    setShowWarning(false);
    setSelectedCompanyId(null);
  };

  const handleGoBack = () => {
    setShowWarning(false);
  };

  // Filter out the current company from the dropdown
  const availableCompanies = companies.filter((c: any) => c.id !== dataItem.companyId);

  // Disable button for new allocations that haven't been saved yet
  const isNewAllocation = !dataItem.allocationId;
  const selectedCompany = companies.find((c: any) => c.id === selectedCompanyId);

  return (
    <td className="k-table-td" style={{ paddingLeft: 0 }}>
      <Button
        size="small"
        icon="paper-plane"
        themeColor="primary"
        fillMode="outline"
        title="Reassign to different company"
        disabled={isNewAllocation}
        onClick={handleSendClick}
      />

      {showDialog && (
        <Dialog title="Reassign" onClose={handleCancel} width={500} closeIcon={false}>
          <div style={{ padding: "20px" }}>
            <div style={{ marginBottom: "20px" }}>
              <Typography.h6>Company:</Typography.h6>
              <DropDownList
                data={availableCompanies}
                dataItemKey="id"
                textField="name"
                value={selectedCompany}
                onChange={(e) => {
                  const companyId = e.value.id;
                  setSelectedCompanyId(companyId);
                }}
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button
                themeColor="secondary"
                fillMode="solid"
                onClick={handleCancel}
                disabled={updateCompanyMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                themeColor="primary"
                onClick={handleConfirm}
                disabled={!selectedCompanyId || updateCompanyMutation.isPending}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Dialog>
      )}

      {showWarning && (
        <Dialog title="Warning ⚠️" onClose={handleCancel} width={500} closeIcon={false}>
          <div style={{ padding: "20px" }}>
            <Typography.h6 style={{ marginBottom: "20px" }}>
              This action cannot be undone!
            </Typography.h6>
            <Typography.p style={{ marginBottom: "20px" }}>
              You are about to reassign this allocation to a different company. This will
              permanently change the company association and cannot be reversed.
            </Typography.p>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <Button
                themeColor="secondary"
                fillMode="solid"
                onClick={handleGoBack}
                disabled={updateCompanyMutation.isPending}
              >
                Go Back
              </Button>
              <Button
                themeColor="primary"
                onClick={handleProceedWithReassignment}
                disabled={updateCompanyMutation.isPending}
              >
                {updateCompanyMutation.isPending ? <LoadingIndicator loading={true} /> : "Proceed"}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </td>
  );
};
